import React from 'react';
import { useTranslation } from '../../i18n';
import LanguageSelector from './LanguageSelector';
import '../../App.css';

interface HeaderProps {
  title?: string;
  leftButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    tooltip?: string;
  };
  rightButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    tooltip?: string;
  };
  rightContent?: React.ReactNode;
  centerContent?: React.ReactNode;
  roomCode?: string;
  subtitle?: string;
  logo?: {
    src: string;
    url: string;
    alt?: string;
  };
  premiumLink?: {
    onClick: () => void;
  };
  isPremium?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title,
  leftButton,
  rightButton,
  rightContent,
  centerContent,
  roomCode,
  subtitle,
  logo,
  premiumLink,
  isPremium = false,
}) => {
  const { t } = useTranslation();
  const [hoveredButton, setHoveredButton] = React.useState<string | null>(null);

  return (
    <header className="app-header">
      <div className="app-header__section">
        {logo ? (
          <a href={logo.url} className="app-header__logo-link">
            <img
              src={logo.src}
              alt={logo.alt || t('common.logo')}
              className="app-header__logo-img"
            />
          </a>
        ) : leftButton ? (
          <>
            <button
              type="button"
              onClick={leftButton.onClick}
              onMouseEnter={() => setHoveredButton('left')}
              onMouseLeave={() => setHoveredButton(null)}
              className="app-header__icon-btn"
              aria-label={leftButton.tooltip}
            >
              {leftButton.icon}
            </button>
            {hoveredButton === 'left' && leftButton.tooltip && (
              <div className="app-header__tooltip app-header__tooltip--left">
                {leftButton.tooltip}
              </div>
            )}
          </>
        ) : (
          <div style={{ minWidth: '50px' }} />
        )}
      </div>

      <div className="app-header__center">
        {centerContent && !roomCode ? (
          centerContent
        ) : (
          <>
            {centerContent && roomCode && (
              <div style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', zIndex: 1002 }}>
                {centerContent}
              </div>
            )}
            {roomCode ? (
              <div className="app-header__room-code-row">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <span className="room-code-label room-code-label-desktop app-header__room-code-label">
                    {isPremium ? (
                      <>
                        <span style={{ color: '#d97706' }}>{t('common.premium')}</span>
                        {' '}{t('lobby.premiumHostSetup.premiumRoomCode')}:
                      </>
                    ) : (
                      t('common.header.roomCodeLabel')
                    )}
                  </span>
                  <div className="app-header__room-code-value">{roomCode}</div>
                </div>
                {premiumLink && (
                  <button type="button" onClick={premiumLink.onClick} className="app-header__premium-link">
                    {t('common.header.havePremiumRoom')}
                  </button>
                )}
              </div>
            ) : title ? (
              <div className="app-header__title-group header-title-subtitle">
                <h1 className="app-header__title">{title}</h1>
                {subtitle && <span className="app-header__subtitle">{subtitle}</span>}
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="app-header__section app-header__section--right app-header__actions" style={{ minWidth: rightContent || rightButton ? '50px' : 'auto' }}>
        <LanguageSelector />
        {rightContent ? (
          rightContent
        ) : rightButton ? (
          <>
            <button
              type="button"
              onClick={rightButton.onClick}
              onMouseEnter={() => setHoveredButton('right')}
              onMouseLeave={() => setHoveredButton(null)}
              className="app-header__icon-btn"
              aria-label={rightButton.tooltip}
            >
              {rightButton.icon}
            </button>
            {hoveredButton === 'right' && rightButton.tooltip && (
              <div className="app-header__tooltip app-header__tooltip--right">
                {rightButton.tooltip}
              </div>
            )}
          </>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
